import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextTemplatePicker from '../../../TextTemplatePicker';
import DocumentTemplateSelector from '../../../DocumentTemplateSelector';
import EmailModal from '../../../EmailModal';
import { apiFetch } from '../../../utils/api';
import { generateDocumentOutput, generateDocumentPdfDataUri } from '../../../utils/documentGeneration';
import { getCompanyVariables } from '../../../utils/templateVariables';

function QuoteDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [quote, setQuote] = useState({
    customer_id: '',
    site_id: '',
    valid_until: '',
    status: 'draft',
    notes: '',
    terms: 'Payment due within 30 days.\nAll prices exclude VAT unless stated.\nQuote valid for 30 days from date of issue.',
    items: []
  });

  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [generatingIntro, setGeneratingIntro] = useState(false);
  const [generatingClosing, setGeneratingClosing] = useState(false);
  const [generatingLineItem, setGeneratingLineItem] = useState(null);
  const [introText, setIntroText] = useState('');
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [emailDocumentTemplate, setEmailDocumentTemplate] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [customerEmail, setCustomerEmail] = useState('');
  const [emailTemplateData, setEmailTemplateData] = useState({});
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteError, setQuoteError] = useState('');

  useEffect(() => {
    fetchCustomers();
    if (!isNew) {
      fetchQuote();
    } else {
      setQuoteError('');
      setLoadingQuote(false);
      // Set default valid_until to 30 days from now
      const date = new Date();
      date.setDate(date.getDate() + 30);
      setQuote(prev => ({ ...prev, valid_until: date.toISOString().split('T')[0] }));
    }
  }, [id]);

  useEffect(() => {
    if (quote.customer_id) {
      fetchSites(quote.customer_id);
    }
  }, [quote.customer_id]);

  const fetchCustomers = async () => {
    try {
      const response = await apiFetch('/api/customers');
      const data = await response.json();
      setCustomers(data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchSites = async (customerId) => {
    try {
      const response = await apiFetch(`/api/sites?customer_id=${customerId}`);
      const data = await response.json();
      setSites(data);
    } catch (error) {
      console.error('Error fetching sites:', error);
    }
  };

  const fetchQuote = async () => {
    setLoadingQuote(true);
    setQuoteError('');
    try {
      const response = await apiFetch(`/api/quotes/${id}`);
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Quote load failed with status ${response.status}`);
      }
      const data = await response.json();
      setQuote(data);
    } catch (error) {
      console.error('Error fetching quote:', error);
      const message = error.response?.data?.error || error.message || 'Could not load quote';
      setQuoteError(message);
    } finally {
      setLoadingQuote(false);
    }
  };

  const addLineItem = () => {
    setQuote(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: '',
          category: 'parts',
          quantity: 1,
          unit: 'each',
          cost_price: 0,
          sell_price: 0
        }
      ]
    }));
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...quote.items];
    newItems[index][field] = value;
    setQuote(prev => ({ ...prev, items: newItems }));
  };

  const removeLineItem = (index) => {
    setQuote(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateLineTotals = (item) => {
    const qty = parseFloat(item.quantity) || 0;
    const cost = parseFloat(item.cost_price) || 0;
    const sell = parseFloat(item.sell_price) || 0;
    const lineCost = qty * cost;
    const lineTotal = qty * sell;
    const lineProfit = lineTotal - lineCost;
    const lineMargin = lineTotal > 0 ? (lineProfit / lineTotal * 100) : 0;
    return { lineCost, lineTotal, lineProfit, lineMargin };
  };

  const calculateTotals = () => {
    let subtotal = 0;
    let totalCost = 0;

    quote.items.forEach(item => {
      const { lineTotal, lineCost } = calculateLineTotals(item);
      subtotal += lineTotal;
      totalCost += lineCost;
    });

    const vatRate = 20.00;
    const vatAmount = subtotal * (vatRate / 100);
    const total = subtotal + vatAmount;
    const totalProfit = subtotal - totalCost;
    const profitMargin = subtotal > 0 ? (totalProfit / subtotal * 100) : 0;

    return { subtotal, vatAmount, total, totalCost, totalProfit, profitMargin };
  };

  const handleSave = async () => {
    if (!quote.customer_id) {
      alert('Please select a customer');
      return;
    }

    setSaving(true);
    try {
      const url = isNew
        ? '/api/quotes'
        : `/api/quotes/${id}`;

      const method = isNew ? 'POST' : 'PUT';

      const response = await apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(quote)
      });

      if (response.ok) {
        const data = await response.json();
        if (isNew && data.id) {
          navigate(`/ops/quotes/${data.id}`);
        } else {
          alert('Quote saved successfully');
          fetchQuote();
        }
      } else {
        alert('Failed to save quote');
      }
    } catch (error) {
      console.error('Error saving quote:', error);
      alert('Error saving quote');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = async () => {
    if (!selectedTemplate) {
      setShowTemplateSelector(true);
      return;
    }
    const outputWindow = window.open('', '_blank');
    if (outputWindow) {
      outputWindow.document.write('<p style="font-family: sans-serif; padding: 20px;">Preparing document...</p>');
    }
    await handleTemplateGenerate(selectedTemplate, 'print', null, outputWindow);
  };

  const handlePDF = async () => {
    if (!selectedTemplate) {
      setShowTemplateSelector(true);
      return;
    }
    await handleTemplateGenerate(selectedTemplate, 'pdf');
  };

  const handleEmail = async (templateOverride = selectedTemplate) => {
    if (!templateOverride) {
      setShowTemplateSelector(true);
      return;
    }

    setSelectedTemplate(templateOverride);
    setEmailDocumentTemplate(templateOverride);

    // Get customer email
    const customer = customers.find(c => c.id === parseInt(quote.customer_id));
    const email = customer?.email || '';

    if (!email) {
      const confirmed = window.confirm('No email address found for this customer. Do you want to continue anyway?');
      if (!confirmed) return;
    }

    setCustomerEmail(email);

    // Prepare email template data
    const emailData = await prepareEmailData();
    setEmailTemplateData(emailData);

    setShowEmailModal(true);
  };

  const prepareEmailData = async () => {
    // Get company settings
    const settingsRes = await apiFetch('/api/company-settings');
    const companySettings = await settingsRes.json();

    const customer = customers.find(c => c.id === parseInt(quote.customer_id));
    const { total } = calculateTotals();

    return {
      customer_name: customer?.company_name || 'Customer',
      quote_number: quote.quote_ref || `Q${quote.id}`,
      quote_date: quote.created_at ? new Date(quote.created_at).toLocaleDateString() : new Date().toLocaleDateString(),
      quote_total: total.toFixed(2),
      valid_until: quote.valid_until || '',
      company_name: companySettings.company_name || 'Your Company',
      company_phone: companySettings.phone || '',
      company_email: companySettings.email || ''
    };
  };

  const handleSendEmail = async ({ to, subject, body }) => {
    try {
      const templateForEmail = emailDocumentTemplate || selectedTemplate;
      if (!templateForEmail?.id) {
        throw new Error('Please select a quote Word template first');
      }

      const templateData = await prepareQuoteData(quote);
      const pdfData = await generateDocumentPdfDataUri(templateForEmail, templateData);

      // Send email with PDF attachment
      const response = await apiFetch('/api/email/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to,
          subject,
          body,
          pdfData,
          pdfFilename: `Quote-${quote.quote_ref || quote.id}.pdf`
        })
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error([error.error, error.details].filter(Boolean).join(': ') || 'Failed to send email');
      }

      alert('Email sent successfully with PDF attachment!');

    } catch (error) {
      console.error('Email error:', error);
      alert('Error sending email: ' + error.message);
    }
  };

  const handleTemplateGenerate = async (template, action, data, outputWindow = null) => {
    try {
      if (action === 'email') {
        setSelectedTemplate(template);
        setShowTemplateSelector(false);
        await handleEmail(template);
        return;
      }

      const templateData = await prepareQuoteData(quote);
      setSelectedTemplate(template);
      await generateDocumentOutput(template, action, templateData, `Quote-${quote.quote_ref || quote.id}`, outputWindow);
      setShowTemplateSelector(false);
    } catch (error) {
      console.error('Template generation error:', error);
      alert('Error generating document: ' + error.message);
    }
  };

  // Prepare quote data for template
  const prepareQuoteData = async (quoteData) => {
    // Fetch company settings
    let companySettings = {};
    try {
      const res = await apiFetch('/api/company-settings');
      companySettings = await res.json();
    } catch (err) {
      console.error('Error fetching company settings:', err);
    }

    // Build company address from parts
    const companyAddressParts = [
      companySettings.address_line1,
      companySettings.address_line2,
      companySettings.city,
      companySettings.county,
      companySettings.postcode
    ].filter(Boolean);
    const companyAddress = companyAddressParts.join(', ');

    return {
      // Customer info
      customer_name: quoteData.customer_name || '',
      customer_address: quoteData.customer_address || '',
      customer_email: quoteData.customer_email || '',
      customer_phone: quoteData.customer_phone || '',

      // Quote info
      quote_number: quoteData.quote_ref || quoteData.id || '',
      quote_date: quoteData.quote_date ? new Date(quoteData.quote_date).toLocaleDateString() : '',
      valid_until: quoteData.valid_until ? new Date(quoteData.valid_until).toLocaleDateString() : '',

      // Site info
      site_address: quoteData.site_address || '',

      // Items
      items: (quoteData.items || []).map(item => ({
        description: item.description || '',
        quantity: item.quantity || 0,
        unit_price: item.unit_price || 0,
        total: (item.quantity || 0) * (item.unit_price || 0)
      })),

      // Financial
      subtotal: `£${parseFloat(quoteData.subtotal || 0).toFixed(2)}`,
      vat_rate: quoteData.vat_rate || '20',
      vat_amount: `£${parseFloat(quoteData.vat_amount || 0).toFixed(2)}`,
      total: `£${parseFloat(quoteData.total || 0).toFixed(2)}`,

      // Terms
      terms: quoteData.terms || '',
      notes: quoteData.notes || '',

      // Company fields (consistent across all templates)
      ...getCompanyVariables(companySettings)
    };
  };

  const handleConvertToJob = async () => {
    if (!window.confirm(`Convert Quote ${quote.quote_ref || quote.quote_number} to a Job?`)) {
      return;
    }

    try {
      const res = await apiFetch(`/api/quotes/${id}/convert-to-job`, {
        method: 'POST'
      });

      const data = await res.json();

      if (res.ok && data.success) {
        alert(`Quote converted to Job ${data.job_number}!`);
        navigate(`/jobs/${data.job_id}`);
      } else {
        alert(data.error || 'Failed to convert quote to job');
      }
    } catch (err) {
      console.error('Error converting quote:', err);
      alert('Error converting quote to job');
    }
  };

  const handleConvertToInvoice = async () => {
    if (!window.confirm(`Create Invoice directly from Quote ${quote.quote_ref || quote.quote_number}?`)) {
      return;
    }

    try {
      const res = await apiFetch(`/api/quotes/${id}/convert-to-invoice`, {
        method: 'POST'
      });

      const data = await res.json();

      if (res.ok && data.success) {
        alert(`Invoice ${data.invoice_ref} created from Quote!`);
        navigate(`/finance/invoices/${data.invoice_id}`);
      } else {
        alert(data.error || 'Failed to create invoice from quote');
      }
    } catch (err) {
      console.error('Error converting quote:', err);
      alert('Error creating invoice from quote');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete quote ${quote.quote_ref || 'this quote'}? This action cannot be undone.`)) {
      return;
    }

    setDeleting(true);
    try {
      const res = await apiFetch(`/api/quotes/${id}`, {
        method: 'DELETE'
      });

      if (res.ok) {
        alert('Quote deleted successfully!');
        navigate('/ops/quotes');
      } else {
        alert('Failed to delete quote');
      }
    } catch (err) {
      console.error('Error deleting quote:', err);
      alert('Error deleting quote');
    }
    setDeleting(false);
  };

  const generateIntroduction = async () => {
    if (!quote.customer_id) {
      alert('Please select a customer first');
      return;
    }

    setGeneratingIntro(true);
    try {
      const customer = customers.find(c => c.id === parseInt(quote.customer_id));
      const site = sites.find(s => s.id === parseInt(quote.site_id));

      const response = await apiFetch('/api/ai/introduction', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: customer?.company_name || 'Customer',
          site_name: site?.site_name || customer?.company_name || 'Site',
          work_type: 'door maintenance',
          industry: 'commercial'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setIntroText(data.text);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to generate introduction');
      }
    } catch (error) {
      console.error('Error generating introduction:', error);
      alert('Error generating introduction');
    } finally {
      setGeneratingIntro(false);
    }
  };

  const generateClosing = async () => {
    setGeneratingClosing(true);
    try {
      const validDays = quote.valid_until ?
        Math.ceil((new Date(quote.valid_until) - new Date()) / (1000 * 60 * 60 * 24)) : 30;

      const response = await apiFetch('/api/ai/closing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          valid_days: validDays,
          urgency: 'normal',
          contact_method: 'phone or email'
        })
      });

      if (response.ok) {
        const data = await response.json();
        setQuote(prev => ({ ...prev, terms: data.text }));
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to generate closing');
      }
    } catch (error) {
      console.error('Error generating closing:', error);
      alert('Error generating closing');
    } finally {
      setGeneratingClosing(false);
    }
  };

  const generateLineItemDescription = async (index) => {
    const item = quote.items[index];
    if (!item.description || item.description.length < 3) {
      alert('Please enter a brief description first');
      return;
    }

    setGeneratingLineItem(index);
    try {
      const response = await apiFetch('/api/ai/line-item', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brief_description: item.description,
          product_type: item.category || 'general'
        })
      });

      if (response.ok) {
        const data = await response.json();
        updateLineItem(index, 'description', data.text);
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to generate description');
      }
    } catch (error) {
      console.error('Error generating line item:', error);
      alert('Error generating line item description');
    } finally {
      setGeneratingLineItem(null);
    }
  };

  const totals = calculateTotals();
  const selectedCustomer = customers.find(c => c.id === parseInt(quote.customer_id));
  const selectedSite = sites.find(s => s.id === parseInt(quote.site_id));

  if (!isNew && loadingQuote) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
          <div className="max-w-full mx-auto px-6 h-full flex items-center justify-between">
            <button
              onClick={() => navigate('/ops/quotes')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              Back
            </button>
            <h1 className="text-4xl font-bold">Loading Quote</h1>
            <div className="w-20" />
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-gray-700">
            Loading quote details...
          </div>
        </div>
      </div>
    );
  }

  if (!isNew && quoteError) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
          <div className="max-w-full mx-auto px-6 h-full flex items-center justify-between">
            <button
              onClick={() => navigate('/ops/quotes')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              Back
            </button>
            <h1 className="text-4xl font-bold">Quote Could Not Load</h1>
            <div className="w-20" />
          </div>
        </div>
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-red-900 mb-2">There was a problem loading this quote.</h2>
            <p className="text-sm text-red-800 mb-4">{quoteError}</p>
            <div className="flex gap-3">
              <button
                onClick={fetchQuote}
                className="px-4 py-2 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition"
              >
                Retry
              </button>
              <button
                onClick={() => navigate('/ops/quotes')}
                className="px-4 py-2 bg-white border border-red-200 text-red-700 rounded text-sm font-medium hover:bg-red-100 transition"
              >
                Back to Quotes
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/ops/quotes')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">
                {isNew ? 'New Quote' : `Quote ${quote.quote_ref || ''}`}
              </h1>
              <span className="text-sm text-blue-100">{quote.status || 'Draft'}</span>
            </div>
            <div className="flex items-center space-x-2">
            {!isNew && quote.job_number && (
              <button
                onClick={() => navigate(`/jobs/${quote.job_id_full}`)}
                className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 transition"
              >
                📋 Job: {quote.job_number}
              </button>
            )}
            {!isNew && quote.invoice_number && !quote.job_number && (
              <button
                onClick={() => navigate(`/finance/invoices/${quote.invoice_id_full}`)}
                className="px-4 py-1.5 bg-green-600 text-white rounded text-sm font-medium hover:bg-green-700 transition"
              >
                💰 Invoice: {quote.invoice_number}
              </button>
            )}
            {!isNew && quote.status === 'accepted' && !quote.job_number && !quote.invoice_number && (
              <>
                <button
                  onClick={handleConvertToJob}
                  className="px-4 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 transition"
                >
                  ➡️ Convert to Job
                </button>
                <button
                  onClick={handleConvertToInvoice}
                  className="px-4 py-1.5 bg-purple-600 text-white rounded text-sm font-medium hover:bg-purple-700 transition"
                >
                  💰 Create Invoice
                </button>
              </>
            )}
            {!isNew && (
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-1.5 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : '🗑️ Delete'}
              </button>
            )}
            <div className="flex-1"></div>
            {!isNew && (
              <>
                <button
                  onClick={() => setShowTemplateSelector(true)}
                  className="px-4 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
                  title={selectedTemplate ? `Template: ${selectedTemplate.name}` : 'Select quote template'}
                >
                  Template: {selectedTemplate ? selectedTemplate.name : 'Select'}
                </button>
                <button
                  onClick={handlePrint}
                  className="px-4 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
                >
                  🖨️ Print
                </button>
                <button
                  onClick={handlePDF}
                  className="px-4 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
                >
                  📄 PDF
                </button>
                <button
                  onClick={handleEmail}
                  className="px-4 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400 transition"
                >
                  ✉️ Email
                </button>
              </>
            )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? '⏳ Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Main Content - 2/3 width */}
          <div className="lg:col-span-2 space-y-4">

            {/* Section 1: Quote Details */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-purple-100 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">1</span>
                Quote Details
              </h2>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Customer *</label>
                  <select
                    value={quote.customer_id}
                    onChange={(e) => setQuote(prev => ({ ...prev, customer_id: e.target.value, site_id: '' }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="">Select customer...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Site</label>
                  <select
                    value={quote.site_id || ''}
                    onChange={(e) => setQuote(prev => ({ ...prev, site_id: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-purple-500"
                    disabled={!quote.customer_id}
                  >
                    <option value="">Head Office</option>
                    {sites.map(s => (
                      <option key={s.id} value={s.id}>{s.site_name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Valid Until</label>
                  <input
                    type="date"
                    value={quote.valid_until || ''}
                    onChange={(e) => setQuote(prev => ({ ...prev, valid_until: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={quote.status}
                    onChange={(e) => setQuote(prev => ({ ...prev, status: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="sent">Sent</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="expired">Expired</option>
                  </select>
                </div>
              </div>

              {/* Introduction Text (Optional) */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">Introduction Text (Optional)</label>
                  <div className="flex items-center gap-2">
                    <TextTemplatePicker
                      category="Quotes"
                      onInsert={(text) => {
                        const current = introText || '';
                        const newText = current ? current + '\n\n' + text : text;
                        setIntroText(newText);
                      }}
                    />
                    <button
                      onClick={generateIntroduction}
                      disabled={generatingIntro || !quote.customer_id}
                      className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {generatingIntro ? '⏳ Generating...' : '✨ AI Generate'}
                    </button>
                  </div>
                </div>
                <textarea
                  value={introText}
                  onChange={(e) => setIntroText(e.target.value)}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-purple-500"
                  placeholder="Optional introduction paragraph for the quote..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  This text will appear at the start of the quote
                </p>
              </div>
            </div>

            {/* Section 2: Line Items */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                  <span className="bg-purple-100 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">2</span>
                  Line Items
                </h2>
                <button
                  onClick={addLineItem}
                  className="px-3 py-1.5 bg-gray-300 text-black rounded text-sm font-medium hover:bg-gray-400"
                >
                  ➕ Add Line
                </button>
              </div>

              {quote.items.length === 0 ? (
                <div className="text-center py-8 text-gray-500 text-sm">
                  No line items yet. Click "Add Line" to start building the quote.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-2 py-2 text-xs font-semibold text-gray-600">Description</th>
                        <th className="text-left px-2 py-2 text-xs font-semibold text-gray-600">Category</th>
                        <th className="text-center px-2 py-2 text-xs font-semibold text-gray-600">Qty</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Cost</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Sell</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Total</th>
                        <th className="text-right px-2 py-2 text-xs font-semibold text-gray-600">Profit</th>
                        <th className="text-center px-2 py-2 text-xs font-semibold text-gray-600"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {quote.items.map((item, index) => {
                        const { lineTotal, lineProfit, lineMargin } = calculateLineTotals(item);
                        return (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-2 py-2">
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={item.description}
                                  onChange={(e) => updateLineItem(index, 'description', e.target.value)}
                                  placeholder="Brief description..."
                                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                                <button
                                  onClick={() => generateLineItemDescription(index)}
                                  disabled={generatingLineItem === index || !item.description}
                                  className="px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50 whitespace-nowrap"
                                  title="Expand with AI"
                                >
                                  {generatingLineItem === index ? '⏳' : '✨'}
                                </button>
                              </div>
                            </td>
                            <td className="px-2 py-2">
                              <select
                                value={item.category}
                                onChange={(e) => updateLineItem(index, 'category', e.target.value)}
                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                              >
                                <option value="parts">Parts</option>
                                <option value="labour">Labour</option>
                                <option value="other">Other</option>
                              </select>
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={item.quantity}
                                onChange={(e) => updateLineItem(index, 'quantity', e.target.value)}
                                className="w-20 px-2 py-1 border border-gray-300 rounded text-sm text-center"
                                min="0"
                                step="0.01"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={item.cost_price}
                                onChange={(e) => updateLineItem(index, 'cost_price', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <input
                                type="number"
                                value={item.sell_price}
                                onChange={(e) => updateLineItem(index, 'sell_price', e.target.value)}
                                className="w-24 px-2 py-1 border border-gray-300 rounded text-sm text-right"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                              />
                            </td>
                            <td className="px-2 py-2 text-right font-medium">
                              £{lineTotal.toFixed(2)}
                            </td>
                            <td className="px-2 py-2 text-right">
                              <span className={lineProfit >= 0 ? 'text-green-600' : 'text-red-600'}>
                                £{lineProfit.toFixed(2)}
                              </span>
                              <div className="text-xs text-gray-500">
                                {lineMargin.toFixed(1)}%
                              </div>
                            </td>
                            <td className="px-2 py-2 text-center">
                              <button
                                onClick={() => removeLineItem(index)}
                                className="text-red-600 hover:text-red-800 text-xs"
                              >
                                🗑️
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Section 3: Terms & Notes */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <span className="bg-purple-100 text-purple-700 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3">3</span>
                Terms & Notes
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Internal Notes</label>
                  <textarea
                    value={quote.notes || ''}
                    onChange={(e) => setQuote(prev => ({ ...prev, notes: e.target.value }))}
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-purple-500"
                    placeholder="Internal notes (not shown to customer)..."
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">Terms & Conditions</label>
                    <button
                      onClick={generateClosing}
                      disabled={generatingClosing}
                      className="px-3 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                    >
                      {generatingClosing ? '⏳ Generating...' : '✨ AI Generate'}
                    </button>
                  </div>
                  <textarea
                    value={quote.terms || ''}
                    onChange={(e) => setQuote(prev => ({ ...prev, terms: e.target.value }))}
                    rows="5"
                    className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-1 focus:ring-purple-500"
                    placeholder="Terms and conditions shown on quote..."
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar - 1/3 width - Sticky Summary */}
          <div className="lg:col-span-1">
            <div className="sticky top-4 space-y-4">

              {/* Customer Info */}
              {selectedCustomer && (
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="font-semibold text-gray-900 mb-3">Customer</h3>
                  <div className="text-sm space-y-1">
                    <div className="font-medium">{selectedCustomer.company_name}</div>
                    {selectedCustomer.contact_name && (
                      <div className="text-gray-600">{selectedCustomer.contact_name}</div>
                    )}
                    {selectedSite ? (
                      <>
                        <div className="text-gray-600 mt-2 font-medium">{selectedSite.site_name}</div>
                        <div className="text-gray-600">{selectedSite.address}</div>
                        <div className="text-gray-600">{selectedSite.city} {selectedSite.postcode}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-gray-600 mt-2">{selectedCustomer.address}</div>
                        <div className="text-gray-600">{selectedCustomer.city} {selectedCustomer.postcode}</div>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Financial Summary */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg shadow p-4 border border-purple-200">
                <h3 className="font-semibold text-gray-900 mb-3">Summary</h3>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-medium">£{totals.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">VAT (20%)</span>
                    <span className="font-medium">£{totals.vatAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-purple-300">
                    <span className="font-semibold text-gray-900">Total</span>
                    <span className="font-bold text-lg text-purple-700">£{totals.total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-purple-300 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Cost</span>
                    <span className="font-medium text-red-600">£{totals.totalCost.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Profit</span>
                    <span className={`font-bold ${totals.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      £{totals.totalProfit.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Margin</span>
                    <span className={`font-bold ${totals.profitMargin >= 30 ? 'text-green-600' : totals.profitMargin >= 20 ? 'text-yellow-600' : 'text-red-600'}`}>
                      {totals.profitMargin.toFixed(1)}%
                    </span>
                  </div>
                </div>

                {totals.profitMargin < 20 && totals.subtotal > 0 && (
                  <div className="mt-3 p-2 bg-yellow-100 border border-yellow-300 rounded text-xs text-yellow-800">
                    ⚠️ Margin below 20% - review pricing
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Template Selector Modal */}
      <DocumentTemplateSelector
        isOpen={showTemplateSelector}
        onClose={() => setShowTemplateSelector(false)}
        documentType="quote"
        placementKey="quote.print_pdf"
        data={quote}
        onGenerate={handleTemplateGenerate}
        showEmailAction
      />

      {/* Email Modal */}
      <EmailModal
        show={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={handleSendEmail}
        documentType="Quote"
        documentNumber={quote.quote_ref || quote.id}
        customerEmail={customerEmail}
        templateData={emailTemplateData}
      />
    </div>
  );
}

export default QuoteDetail;
